import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext, AccessContext } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { 
  Download, TrendingUp, FileSpreadsheet, Settings2, Plus, Trash2, 
  Play, Save, FileText, Table, Calendar, Filter, Eye, Lock
} from 'lucide-react';

// Get current date in IST timezone
const getISTDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Get start of month in IST timezone
const getISTMonthStart = () => {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  istDate.setDate(1);
  return istDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Get date X months ago in IST
const getDateMonthsAgo = (months) => {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  istDate.setMonth(istDate.getMonth() - months);
  istDate.setDate(1);
  return istDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Column definitions for each report type
const reportTypes = {
  daily_sales: {
    label: 'Daily Sales Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'ms_opening', label: 'MS Opening Reading', default: false },
      { id: 'ms_closing', label: 'MS Closing Reading', default: false },
      { id: 'ms_sales', label: 'MS Sales (L)', default: true },
      { id: 'ms_rate', label: 'MS Rate (₹)', default: false },
      { id: 'ms_revenue', label: 'MS Revenue (₹)', default: true },
      { id: 'hsd_opening', label: 'HSD Opening Reading', default: false },
      { id: 'hsd_closing', label: 'HSD Closing Reading', default: false },
      { id: 'hsd_sales', label: 'HSD Sales (L)', default: true },
      { id: 'hsd_rate', label: 'HSD Rate (₹)', default: false },
      { id: 'hsd_revenue', label: 'HSD Revenue (₹)', default: true },
      { id: 'total_sales', label: 'Total Sales (L)', default: true },
      { id: 'total_revenue', label: 'Total Revenue (₹)', default: true }
    ]
  },
  expenses: {
    label: 'Expenses Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'category', label: 'Category', default: true },
      { id: 'description', label: 'Description', default: true },
      { id: 'amount', label: 'Amount (₹)', default: true },
      { id: 'paid_to', label: 'Paid To', default: true },
      { id: 'payment_method', label: 'Payment Method', default: true },
      { id: 'created_by', label: 'Created By', default: false }
    ]
  },
  station_ledger: {
    label: 'Station Ledger Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'entry_type', label: 'Entry Type', default: true },
      { id: 'description', label: 'Description', default: true },
      { id: 'debit', label: 'Debit (₹)', default: true },
      { id: 'credit', label: 'Credit (₹)', default: true },
      { id: 'balance', label: 'Balance (₹)', default: true },
      { id: 'payment_method', label: 'Payment Method', default: false },
      { id: 'fuel_type', label: 'Fuel Type', default: false },
      { id: 'quantity', label: 'Quantity (L)', default: false },
      { id: 'customer_name', label: 'Customer Name', default: false }
    ]
  },
  customer_ledger: {
    label: 'Customer Ledger Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'customer_name', label: 'Customer Name', default: true },
      { id: 'firm_name', label: 'Firm Name', default: true },
      { id: 'mobile', label: 'Mobile', default: false },
      { id: 'entry_type', label: 'Entry Type', default: true },
      { id: 'description', label: 'Description', default: true },
      { id: 'debit', label: 'Debit (₹)', default: true },
      { id: 'credit', label: 'Credit (₹)', default: true },
      { id: 'balance', label: 'Balance (₹)', default: true },
      { id: 'payment_method', label: 'Payment Method', default: false }
    ]
  },
  tt_deliveries: {
    label: 'TT Deliveries Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'ms_quantity', label: 'MS Quantity (L)', default: true },
      { id: 'ms_unit_price', label: 'MS Rate (₹)', default: true },
      { id: 'ms_total_amount', label: 'MS Amount (₹)', default: true },
      { id: 'hsd_quantity', label: 'HSD Quantity (L)', default: true },
      { id: 'hsd_unit_price', label: 'HSD Rate (₹)', default: true },
      { id: 'hsd_total_amount', label: 'HSD Amount (₹)', default: true },
      { id: 'total_quantity', label: 'Total Quantity (L)', default: false },
      { id: 'total_amount', label: 'Total Amount (₹)', default: true }
    ]
  },
  fuel_losses: {
    label: 'Fuel Stock & Losses Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'ms_stock', label: 'MS Dip Stock (L)', default: true },
      { id: 'ms_expected', label: 'MS Expected (L)', default: false },
      { id: 'ms_loss', label: 'MS Loss/Gain (L)', default: true },
      { id: 'ms_loss_value', label: 'MS Loss Value (₹)', default: true },
      { id: 'hsd_stock', label: 'HSD Dip Stock (L)', default: true },
      { id: 'hsd_expected', label: 'HSD Expected (L)', default: false },
      { id: 'hsd_loss', label: 'HSD Loss/Gain (L)', default: true },
      { id: 'hsd_loss_value', label: 'HSD Loss Value (₹)', default: true }
    ]
  },
  customer_sales: {
    label: 'Customer Sales Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'customer_name', label: 'Customer Name', default: true },
      { id: 'firm_name', label: 'Firm Name', default: true },
      { id: 'vehicle_number', label: 'Vehicle Number', default: false },
      { id: 'fuel_type', label: 'Fuel Type', default: true },
      { id: 'quantity', label: 'Quantity (L)', default: true },
      { id: 'unit_price', label: 'Rate (₹)', default: true },
      { id: 'gross_amount', label: 'Gross Amount (₹)', default: false },
      { id: 'discount', label: 'Discount (₹)', default: false },
      { id: 'net_amount', label: 'Net Amount (₹)', default: true },
      { id: 'is_credit', label: 'Payment Type', default: true },
      { id: 'payment_method', label: 'Payment Method', default: false }
    ]
  },
  withdrawals: {
    label: 'Withdrawals Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'partner_name', label: 'Partner Name', default: true },
      { id: 'amount', label: 'Amount (₹)', default: true },
      { id: 'description', label: 'Description', default: true },
      { id: 'payment_method', label: 'Payment Method', default: true },
      { id: 'created_by', label: 'Created By', default: false }
    ]
  },
  e_charging: {
    label: 'E-Charging Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'bill_number', label: 'Bill No.', default: true },
      { id: 'charging_point_name', label: 'Charger', default: true },
      { id: 'vehicle_number', label: 'Vehicle', default: true },
      { id: 'energy_consumed_kwh', label: 'Energy (kWh)', default: true },
      { id: 'charging_duration_mins', label: 'Duration (min)', default: false },
      { id: 'total_amount', label: 'Revenue (₹)', default: true },
      { id: 'grid_cost', label: 'Grid Cost (₹)', default: true },
      { id: 'profit', label: 'Profit (₹)', default: true },
      { id: 'payment_method', label: 'Payment', default: false },
      { id: 'customer_name', label: 'Customer', default: false }
    ]
  },
  lubricant_profit: {
    label: 'Lubricant Profit Report',
    columns: [
      { id: 'date', label: 'Date', default: true },
      { id: 'bill_number', label: 'Bill No.', default: true },
      { id: 'customer_name', label: 'Customer', default: true },
      { id: 'items_count', label: 'Items', default: true },
      { id: 'subtotal', label: 'Subtotal (₹)', default: false },
      { id: 'total_gst', label: 'GST (₹)', default: false },
      { id: 'total_amount', label: 'Revenue (₹)', default: true },
      { id: 'total_cost', label: 'Cost (₹)', default: true },
      { id: 'profit', label: 'Profit (₹)', default: true },
      { id: 'payment_method', label: 'Payment', default: false }
    ]
  },
  inventory_aging: {
    label: 'Inventory Aging Report',
    columns: [
      { id: 'product_name', label: 'Product', default: true },
      { id: 'brand', label: 'Brand', default: true },
      { id: 'pack_size_ml', label: 'Pack Size (ml)', default: true },
      { id: 'current_stock', label: 'Stock', default: true },
      { id: 'cost_price', label: 'Cost Price (₹)', default: true },
      { id: 'stock_value', label: 'Stock Value (₹)', default: true },
      { id: 'days_in_stock', label: 'Days in Stock', default: true },
      { id: 'days_to_expiry', label: 'Days to Expiry', default: true },
      { id: 'aging_category', label: 'Status', default: true }
    ]
  }
};

function Reports() {
  const { user } = useContext(AuthContext);
  const { accessSettings } = useContext(AccessContext);
  
  const [activeTab, setActiveTab] = useState('quick-export');
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState(getISTMonthStart());
  const [endDate, setEndDate] = useState(getISTDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Custom report state
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [configDialog, setConfigDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Get report access settings for current user
  const userRole = user?.role || 'viewer';
  const reportAccess = accessSettings?.report_access?.[userRole] || { can_view: true, can_download: false, months_allowed: 1 };
  const canDownload = reportAccess.can_download;
  const monthsAllowed = reportAccess.months_allowed || 1;
  const minAllowedDate = getDateMonthsAgo(monthsAllowed);
  
  const [newConfig, setNewConfig] = useState({
    report_name: '',
    report_type: 'daily_sales',
    columns: reportTypes.daily_sales.columns.filter(c => c.default).map(c => c.id),
    filters: {}
  });

  useEffect(() => {
    fetchReport();
    fetchSavedConfigs();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/reports/analytics?period=${period}&start_date=${startDate}&end_date=${endDate}`
      );
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedConfigs = async () => {
    try {
      const res = await axios.get(`${API}/report-configs`);
      setSavedConfigs(res.data.configs || []);
    } catch (error) {
      console.error('Failed to load report configs');
    }
  };

  const exportPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/export-pdf?period=${period}&start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${period}_${startDate}_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportQuickCSV = async (type) => {
    try {
      let endpoint = '';
      switch(type) {
        case 'daily_sales':
          endpoint = `/export/daily-sales?start_date=${startDate}&end_date=${endDate}`;
          break;
        case 'expenses':
          endpoint = `/export/expenses?start_date=${startDate}&end_date=${endDate}`;
          break;
        case 'station_ledger':
          endpoint = `/export/station-ledger?start_date=${startDate}&end_date=${endDate}`;
          break;
        default:
          endpoint = `/export/daily-sales?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const response = await axios.get(`${API}${endpoint}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.replace(/_/g, ' ')} CSV downloaded`);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleReportTypeChange = (type) => {
    setNewConfig({
      ...newConfig,
      report_type: type,
      columns: reportTypes[type].columns.filter(c => c.default).map(c => c.id)
    });
  };

  const toggleColumn = (columnId) => {
    const columns = newConfig.columns.includes(columnId)
      ? newConfig.columns.filter(c => c !== columnId)
      : [...newConfig.columns, columnId];
    setNewConfig({ ...newConfig, columns });
  };

  const selectAllColumns = () => {
    setNewConfig({
      ...newConfig,
      columns: reportTypes[newConfig.report_type].columns.map(c => c.id)
    });
  };

  const deselectAllColumns = () => {
    setNewConfig({ ...newConfig, columns: [] });
  };

  const saveReportConfig = async () => {
    if (!newConfig.report_name.trim()) {
      toast.error('Please enter a report name');
      return;
    }
    if (newConfig.columns.length === 0) {
      toast.error('Please select at least one column');
      return;
    }
    
    try {
      await axios.post(`${API}/report-configs`, newConfig);
      toast.success('Report template saved');
      setConfigDialog(false);
      setNewConfig({
        report_name: '',
        report_type: 'daily_sales',
        columns: reportTypes.daily_sales.columns.filter(c => c.default).map(c => c.id),
        filters: {}
      });
      fetchSavedConfigs();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const deleteConfig = async (configId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/report-configs/${configId}`);
      toast.success('Template deleted');
      fetchSavedConfigs();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const previewReport = async (config) => {
    setPreviewLoading(true);
    setPreviewDialog(true);
    setPreviewData([]);
    
    try {
      const response = await axios.post(
        `${API}/reports/generate-custom?config_id=${config.id}&start_date=${startDate}&end_date=${endDate}&format=json`
      );
      setPreviewData(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateCustomReport = async (configId, format = 'csv') => {
    try {
      const response = await axios.post(
        `${API}/reports/generate-custom?config_id=${configId}&start_date=${startDate}&end_date=${endDate}&format=${format}`,
        {},
        { responseType: format === 'csv' ? 'blob' : 'json' }
      );
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const config = savedConfigs.find(c => c.id === configId);
        link.setAttribute('download', `${config?.report_name || 'custom'}_${startDate}_${endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Reports</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="quick-export" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Quick Export
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              Custom Reports
            </TabsTrigger>
          </TabsList>

          {/* Date Range - Common for all tabs */}
          <Card className="p-4 mt-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              {activeTab === 'analytics' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Period</label>
                    <Select value={period} onValueChange={setPeriod}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Generate'}
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {data && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <p className="text-sm text-muted">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(data.total_revenue)}</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-sm text-muted">Total Sales</p>
                    <p className="text-2xl font-bold">{(data.total_fuel_sales || 0).toLocaleString('en-IN')} L</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-sm text-muted">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(data.total_expenses)}</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-sm text-muted">Net Profit</p>
                    <p className={`text-2xl font-bold ${(data.total_revenue - data.total_expenses) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(data.total_revenue - data.total_expenses)}
                    </p>
                  </Card>
                </div>

                {/* Charts */}
                {data.daily_data && data.daily_data.length > 0 && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4">Revenue Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.daily_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d + 'Z').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })} />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#10B981" name="Revenue" />
                        <Line type="monotone" dataKey="expenses" stroke="#EF4444" name="Expenses" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}

                {canDownload ? (
                  <div className="flex gap-2">
                    <Button onClick={exportPDF} variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted p-3 bg-secondary rounded-lg">
                    <Lock className="w-4 h-4" />
                    Download access restricted. Contact admin for permissions.
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Quick Export Tab */}
          <TabsContent value="quick-export" className="space-y-4">
            {!canDownload && (
              <div className="flex items-center gap-2 text-sm text-amber-700 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <Lock className="w-4 h-4" />
                Download access is restricted for your role. Contact administrator for permissions.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Daily Sales</h3>
                    <p className="text-sm text-muted">Totalizer readings & sales</p>
                  </div>
                </div>
                <Button 
                  onClick={() => exportQuickCSV('daily_sales')} 
                  className="w-full" 
                  variant="outline"
                  disabled={!canDownload}
                >
                  {canDownload ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  {canDownload ? 'Download CSV' : 'Restricted'}
                </Button>
              </Card>

              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FileSpreadsheet className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Expenses</h3>
                    <p className="text-sm text-muted">All expense records</p>
                  </div>
                </div>
                <Button 
                  onClick={() => exportQuickCSV('expenses')} 
                  className="w-full" 
                  variant="outline"
                  disabled={!canDownload}
                >
                  {canDownload ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  {canDownload ? 'Download CSV' : 'Restricted'}
                </Button>
              </Card>

              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Station Ledger</h3>
                    <p className="text-sm text-muted">Complete ledger entries</p>
                  </div>
                </div>
                <Button 
                  onClick={() => exportQuickCSV('station_ledger')} 
                  className="w-full" 
                  variant="outline"
                  disabled={!canDownload}
                >
                  {canDownload ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  {canDownload ? 'Download CSV' : 'Restricted'}
                </Button>
              </Card>
            </div>
          </TabsContent>

          {/* Custom Reports Tab */}
          <TabsContent value="custom" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted">Create custom report templates with selected columns</p>
              <Dialog open={configDialog} onOpenChange={setConfigDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Report Template</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Template Name</label>
                      <Input
                        placeholder="e.g., Monthly MS Sales Summary"
                        value={newConfig.report_name}
                        onChange={(e) => setNewConfig({ ...newConfig, report_name: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Report Type</label>
                      <Select value={newConfig.report_type} onValueChange={handleReportTypeChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(reportTypes).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium">Select Columns</label>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={selectAllColumns}>Select All</Button>
                          <Button size="sm" variant="outline" onClick={deselectAllColumns}>Clear All</Button>
                        </div>
                      </div>
                      <div className="border border-border rounded-lg p-4 max-h-64 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-3">
                          {reportTypes[newConfig.report_type].columns.map((col) => (
                            <div key={col.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={col.id}
                                checked={newConfig.columns.includes(col.id)}
                                onCheckedChange={() => toggleColumn(col.id)}
                              />
                              <label htmlFor={col.id} className="text-sm cursor-pointer">
                                {col.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted mt-2">
                        {newConfig.columns.length} columns selected
                      </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setConfigDialog(false)}>Cancel</Button>
                      <Button onClick={saveReportConfig}>
                        <Save className="w-4 h-4 mr-2" />
                        Save Template
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Saved Templates */}
            {savedConfigs.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted mb-4" />
                <h3 className="font-semibold mb-2">No Saved Templates</h3>
                <p className="text-muted mb-4">Create your first custom report template to get started</p>
                <Button onClick={() => setConfigDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedConfigs.map((config) => (
                  <Card key={config.id} className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold">{config.report_name}</h3>
                        <p className="text-sm text-muted">{reportTypes[config.report_type]?.label || config.report_type}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => deleteConfig(config.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-xs text-muted mb-1">Columns ({config.columns?.length || 0}):</p>
                      <div className="flex flex-wrap gap-1">
                        {(config.columns || []).slice(0, 5).map((col) => {
                          const colDef = reportTypes[config.report_type]?.columns.find(c => c.id === col);
                          return (
                            <span key={col} className="px-2 py-0.5 bg-secondary rounded text-xs">
                              {colDef?.label || col}
                            </span>
                          );
                        })}
                        {(config.columns?.length || 0) > 5 && (
                          <span className="px-2 py-0.5 bg-secondary rounded text-xs">
                            +{config.columns.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => previewReport(config)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => generateCustomReport(config.id)}
                        disabled={!canDownload}
                      >
                        {canDownload ? <Download className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                        {canDownload ? 'Export CSV' : 'Restricted'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Report Preview</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto">
              {previewLoading ? (
                <div className="flex items-center justify-center h-48">
                  <p className="text-muted">Loading preview...</p>
                </div>
              ) : previewData.length === 0 ? (
                <div className="flex items-center justify-center h-48">
                  <p className="text-muted">No data found for the selected date range</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border">
                    <thead className="bg-secondary">
                      <tr>
                        {Object.keys(previewData[0] || {}).map((key) => (
                          <th key={key} className="px-3 py-2 text-left border-b border-border font-medium">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 20).map((row, idx) => (
                        <tr key={idx} className="hover:bg-secondary/50">
                          {Object.values(row).map((val, vidx) => (
                            <td key={vidx} className="px-3 py-2 border-b border-border">
                              {typeof val === 'number' ? val.toLocaleString('en-IN') : String(val || '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 20 && (
                    <p className="text-center text-muted py-2 text-sm">
                      Showing first 20 of {previewData.length} rows
                    </p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default Reports;
